import {
  Stack,
  Paper,
  TextField,
  Box,
  Grid,
  Input,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  InputLabel,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import CTAButton from "components/common/CTAButton";
import RevaText from "components/common/RevaText";
import UserContext from "context/UserContext";
import dayjs from "dayjs";
import { competitionsUpdateRoute } from "network/api-routes";
import countriesData from "constants/countries.json";
import performRequest from "network/perform-request";
import { useSnackbar } from "notistack";
import { useContext, useState } from "react";
import { useIntl } from "react-intl";
import {
  accountCurrencies,
  accountsInCurrency,
  cobrosLocked,
  cobrosLockReasonId,
  uniqueAccountInCurrency,
} from "./competition-accounts";

const SectionContainer = ({ title, children }) => (
  <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
    {title && <RevaText id={title} variant="h4" mb={2} />}
    {children}
  </Paper>
);

// Gestión Ajustes does not edit these: organization/country/organizer belong to regular tournaments and payments
// live in Finanzas. They are kept out of the payload so the backend leaves the stored values untouched.
export const GESTION_OMITTED_FIELDS = [
  "organization_id",
  "country",
  "organizer",
  "organizer_phone",
  "online_payments",
  "only_online_payment",
  "payqr_enabled",
  "signups_expires",
  "signups_expires_after",
  "account_id",
];

export default function EditCompetitionForm({
  competition,
  hideDates = false,
  hideTitle = false,
  gestion = false,
  onSubmitResponse,
}) {
  const { user } = useContext(UserContext);
  const intl = useIntl();
  const [startDate, setStartDate] = useState(dayjs(competition.start_at));
  const [endDate, setEndDate] = useState(dayjs(competition.end_at));
  const selectedCustomer = competition.customer?.data;
  const selectedAccount = competition.account?.data;
  const customerAccounts = selectedCustomer?.accounts?.data || selectedCustomer?.accounts || [];
  const subscription = competition.subscription?.data || competition.subscription;
  const subscriptionAccountId = subscription?.account_id;
  const subscriptionAccount = customerAccounts.find((account) => account.id === subscriptionAccountId);
  const locked = cobrosLocked(competition);
  const signupCurrencies = accountCurrencies(customerAccounts);
  const [signupCurrency, setSignupCurrency] = useState(
    selectedAccount?.currency_code || competition.currency_code || ""
  );
  const [formData, setFormData] = useState({
    ...competition,
    bannerUrl: competition.full_banner_url,
    coverUrl: competition.full_cover_url,
    payqrUrl: competition.full_payqr_url,
  });
  const [submitting, setSubmitting] = useState(null);
  const cobrosAccounts = signupCurrency ? accountsInCurrency(customerAccounts, signupCurrency) : [];
  const selectedCobrosAccount = cobrosAccounts.find((account) => account.id === formData.account_id) ?? null;
  const { enqueueSnackbar } = useSnackbar();

  const stepTitles = [
    intl.formatMessage({ id: "Información básica" }),
    intl.formatMessage({ id: "Detalles del evento" }),
    intl.formatMessage({ id: "Opciones de pago" }),
    intl.formatMessage({ id: "Redes sociales" }),
  ];

  const handleFileUpload = (e, type, maxSize, errorMsg) => {
    const file = e.target.files[0];
    if (file && file.size > maxSize) {
      // setError(errorMsg);
      return;
    }

    if (type === "banner" || type === "cover" || type === "payqr") {
      let url = null;
      if (file) {
        url = URL.createObjectURL(file);
      }

      setFormData({ ...formData, [type]: file, [`${type}Url`]: url });
    } else {
      setFormData({ ...formData, [type]: file });
    }
  };

  const saveChanges = () => {
    const headers = { "Content-Type": "multipart/form-data" };
    const formDataToSend = new FormData();

    setSubmitting(true);

    // Recorrer cada propiedad del objeto y agregarla a FormData
    Object.keys(formData).forEach((key) => {
      if (formData[key] !== null) {
        if (formData[key] instanceof File) {
          switch (key) {
            case "banner":
              formDataToSend.append(key, formData[key], `${formData.name}-banner.jpg`);
              break;
            case "cover":
              formDataToSend.append(key, formData[key], `${formData.name}-cover.jpg`);
              break;
            case "regulations":
              formDataToSend.append(key, formData[key], `${formData.name}-regulations.pdf`);
              break;
            case "payqr":
              formDataToSend.append(key, formData[key], `${formData.name}-payqr.png`);
              break;
            default:
              break;
          }
        } else {
          formDataToSend.append(key, formData[key]);
        }
      }
    });

    formDataToSend.append("competition_id", competition.id);
    formDataToSend.delete("banner_url");
    formDataToSend.delete("cover_url");
    formDataToSend.delete("payqr_url");
    formDataToSend.delete("regulations_url");
    formDataToSend.delete("subscription_id");
    formDataToSend.delete("subscription_account_id");
    formDataToSend.delete("customer_id");
    formDataToSend.delete("currency_code");
    formDataToSend.delete("signups_count");
    if (gestion) {
      GESTION_OMITTED_FIELDS.forEach((field) => formDataToSend.delete(field));
    }

    performRequest("POST", competitionsUpdateRoute, formDataToSend, true, user, null, headers)
      .then((response) => {
        setSubmitting(false);
        enqueueSnackbar(intl.formatMessage({ id: "Los cambios se guardaron correctamente" }), { variant: "success" });
        onSubmitResponse?.(response.data?.data || formData);
      })
      .catch((error) => {
        setSubmitting(false);
        enqueueSnackbar(
          error?.response?.data?.message || intl.formatMessage({ id: "No se pudieron guardar los cambios" }),
          {
            variant: "error",
          }
        );
        console.log(error);
      });
  };

  return (
    <Stack sx={{ gap: 3 }}>
      {!hideTitle ? <RevaText id="Editar evento" variant="h2" sx={{ mb: 2 }} /> : null}

      <SectionContainer title={stepTitles[0]}>
        <TextField
          required
          label={intl.formatMessage({ id: "Nombre del evento" })}
          fullWidth
          margin="normal"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        />
        <TextField
          label={intl.formatMessage({ id: "Descripción" })}
          fullWidth
          multiline
          rows={4}
          margin="normal"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
        />
        {!gestion && (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            columnGap: 3,
          }}
        >
          {Array.isArray(user.organizations) && user.organizations.length > 0 && (
            <FormControl fullWidth margin="normal">
              <InputLabel id="organization">{intl.formatMessage({ id: "Organización" })}</InputLabel>
              <Select
                label={intl.formatMessage({ id: "Organización" })}
                labelId="organization"
                value={formData.organization_id}
                onChange={(event) => {
                  const organization_id = event.target.value;
                  setFormData((prev) => ({ ...prev, organization_id }));
                }}
              >
                {user.organizations.map((org) => (
                  <MenuItem value={org.id}>{org.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Autocomplete
            id="country-select"
            options={countriesData}
            autoHighlight
            getOptionLabel={(option) => option.name}
            value={countriesData.find((c) => c.name === formData.country) || null}
            onChange={(event, newValue) => {
              setFormData({
                ...formData,
                country: newValue?.name || "",
              });
            }}
            renderOption={(props, option) => {
              const { key, ...optionProps } = props;
              return (
                <Box key={key} component="li" sx={{ "& > img": { mr: 2, flexShrink: 0 } }} {...optionProps}>
                  {option.name}
                </Box>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={intl.formatMessage({ id: "País" })}
                margin="normal"
                inputProps={{
                  ...params.inputProps,
                  autoComplete: "new-password", // disable autocomplete and autofill
                }}
              />
            )}
            fullWidth
          />
          <TextField
            label={intl.formatMessage({ id: "Organizador" })}
            fullWidth
            margin="normal"
            value={formData.organizer}
            onChange={(e) => setFormData({ ...formData, organizer: e.target.value })}
          />
          <TextField
            label={intl.formatMessage({ id: "Teléfono del organizador" })}
            fullWidth
            margin="normal"
            value={formData.organizer_phone}
            onChange={(e) =>
              setFormData({
                ...formData,
                organizer_phone: e.target.value,
              })
            }
          />
        </Box>
        )}
      </SectionContainer>

      <SectionContainer title={stepTitles[1]}>
        <Stack sx={{ gap: 2 }}>
          {!hideDates ? (
            <Grid container columnGap={2}>
              <Grid xs={12} sm>
                <DatePicker
                  label={`${intl.formatMessage({ id: "Fecha de inicio" })}*`}
                  value={startDate}
                  onChange={(date) => {
                    setStartDate(date);
                    setFormData({
                      ...formData,
                      start_at: dayjs(date).format("YYYY-MM-DD HH:mm:ss"),
                      end_at:
                        endDate && date && endDate < date
                          ? dayjs(date).add(1, "d").format("YYYY-MM-DD HH:mm:ss")
                          : formData.end_at,
                    });
                  }}
                  minDate={null}
                  maxDate={endDate}
                  slotProps={{
                    textField: { fullWidth: true, margin: "normal" },
                  }}
                />
              </Grid>
              <Grid xs={12} sm>
                <DatePicker
                  label={`${intl.formatMessage({ id: "Fecha de fin" })}*`}
                  value={endDate}
                  onChange={(date) => {
                    setEndDate(date);
                    setFormData({
                      ...formData,
                      end_at: dayjs(date).add(1, "d").format("YYYY-MM-DD HH:mm:ss"),
                      start_at:
                        startDate && date && startDate > date
                          ? dayjs(date).format("YYYY-MM-DD HH:mm:ss")
                          : formData.start_at,
                    });
                  }}
                  minDate={startDate}
                  slotProps={{
                    textField: { fullWidth: true, margin: "normal" },
                  }}
                />
              </Grid>
            </Grid>
          ) : null}

          <Grid container gap={2}>
            <Grid container xs={12} sm gap={2}>
              <Grid xs={12} justifyItems="center">
                <Box
                  sx={{
                    alignSelf: "center",
                    width: "216px",
                    height: "270px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    borderRadius: "8px",
                    objectFit: "cover",
                    overflow: "hidden",
                    border: "2px dashed #ccc",
                  }}
                >
                  {formData.bannerUrl ? (
                    <img
                      src={formData.bannerUrl}
                      alt="Vista previa"
                      style={{
                        width: "216px",
                        borderRadius: "8px",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <RevaText id="Formato 4:5" variant="h4" />
                  )}
                </Box>
              </Grid>
              <Grid xs>
                <RevaText id="Poster del torneo (.jpg)" variant="subtitle1" mb={2} />
                <Input
                  fullWidth
                  type="file"
                  inputProps={{ accept: "image/jpg" }}
                  onChange={(e) =>
                    handleFileUpload(
                      e,
                      "banner",
                      1048576,
                      intl.formatMessage({ id: "El poster no puede pesar más de 800KB" })
                    )
                  }
                />
              </Grid>
            </Grid>

            <Grid container xs={12} sm gap={2}>
              <Grid xs={12} justifyItems="center">
                <Box
                  sx={{
                    width: { xs: "100%", sm: "480px" },
                    aspectRatio: "16/9",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    borderRadius: "8px",
                    objectFit: "cover",
                    overflow: "hidden",
                    border: "2px dashed #ccc",
                  }}
                >
                  {formData.coverUrl ? (
                    <img
                      src={formData.coverUrl}
                      alt="Vista previa"
                      style={{
                        width: "100%",
                        borderRadius: "8px",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <RevaText id="Formato 16:9" variant="h4" />
                  )}
                </Box>
              </Grid>
              <Grid xs>
                <RevaText id="Cover del torneo (.jpg)" variant="subtitle1" mb={2} />
                <Input
                  fullWidth
                  type="file"
                  inputProps={{ accept: "image/jpg" }}
                  onChange={(e) =>
                    handleFileUpload(
                      e,
                      "cover",
                      1048576,
                      intl.formatMessage({ id: "El cover no puede pesar más de 800KB" })
                    )
                  }
                />
              </Grid>
            </Grid>
          </Grid>

          <Box width="100%">
            <RevaText id="Reglamento (.pdf)" variant="subtitle1" mb={2} />
            <Input
              fullWidth
              type="file"
              inputProps={{ accept: "application/pdf" }}
              onChange={(e) =>
                handleFileUpload(
                  e,
                  "regulations",
                  1048576,
                  intl.formatMessage({ id: "El reglamento no puede pesar más de 1MB" })
                )
              }
            />
          </Box>
        </Stack>
      </SectionContainer>

      {!gestion && (
      <SectionContainer title={stepTitles[2]}>
        <Grid container gap={2}>
          <Grid xs={12} sm>
            <Autocomplete
              disabled
              sx={{ mt: 2 }}
              value={selectedCustomer}
              getOptionLabel={(option) => option.name}
              renderInput={(params) => (
                <TextField {...params} label={intl.formatMessage({ id: "Cliente" })} variant="outlined" />
              )}
            />
          </Grid>
          <Grid xs={12} sm>
            <TextField
              disabled
              sx={{ mt: 2 }}
              fullWidth
              label={intl.formatMessage({ id: "Cuenta de suscripción" })}
              value={
                subscriptionAccount
                  ? `[${subscriptionAccount.currency_code}] ${subscriptionAccount.name}`
                  : ""
              }
            />
          </Grid>
          <Grid xs={12} sm>
            {locked ? (
              <>
                <TextField
                  disabled
                  sx={{ mt: 2 }}
                  fullWidth
                  label={intl.formatMessage({ id: "¿En qué moneda serán las inscripciones?" })}
                  value={selectedAccount?.currency_code || signupCurrency}
                />
                <Autocomplete
                  disabled
                  sx={{ mt: 2 }}
                  value={selectedAccount}
                  getOptionLabel={(option) => `[${option.currency_code}] ${option.name}`}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={intl.formatMessage({
                        id: "¿Qué cuenta de inscripciones usamos?",
                      })}
                      variant="outlined"
                    />
                  )}
                />
                {cobrosLockReasonId(competition) && (
                  <RevaText id={cobrosLockReasonId(competition)} variant="body2" color="textSecondary" sx={{ mt: 1 }} />
                )}
              </>
            ) : (
              <>
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <InputLabel id="signup-currency">
                    {intl.formatMessage({ id: "¿En qué moneda serán las inscripciones?" })}
                  </InputLabel>
                  <Select
                    labelId="signup-currency"
                    label={intl.formatMessage({ id: "¿En qué moneda serán las inscripciones?" })}
                    value={signupCurrency}
                    onChange={(event) => {
                      const currency = event.target.value;
                      setSignupCurrency(currency);
                      const account = uniqueAccountInCurrency(customerAccounts, currency);
                      setFormData((prev) => ({ ...prev, account_id: account?.id ?? null }));
                    }}
                    disabled={signupCurrencies.length <= 1}
                  >
                    {signupCurrencies.map((currency) => (
                      <MenuItem key={currency} value={currency}>
                        {currency}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {cobrosAccounts.length > 1 && (
                  <>
                    <RevaText
                      id="Tienes más de una cuenta en esta moneda"
                      variant="body2"
                      color="textSecondary"
                      sx={{ mt: 1 }}
                    />
                    <Autocomplete
                      sx={{ mt: 2 }}
                      value={selectedCobrosAccount}
                      options={cobrosAccounts}
                      getOptionLabel={(option) => `[${option.currency_code}] ${option.name}`}
                      renderOption={(props, option) => (
                        <li {...props}>{`[${option.currency_code}] ${option.name}`}</li>
                      )}
                      onChange={(event, value) => {
                        setFormData((prev) => ({ ...prev, account_id: value?.id ?? null }));
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={intl.formatMessage({ id: "¿Qué cuenta de inscripciones usamos?" })}
                          variant="outlined"
                        />
                      )}
                    />
                  </>
                )}
              </>
            )}
          </Grid>
          <Grid xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.only_online_payment}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      only_online_payment: e.target.checked,
                    })
                  }
                  value="onlyOnlinePayment"
                />
              }
              label={intl.formatMessage({ id: "Solo permitir pagos online" })}
            />
          </Grid>
          <Grid xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.payqr_enabled}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      payqr_enabled: e.target.checked,
                    })
                  }
                />
              }
              label={intl.formatMessage({ id: "Pagos con QR" })}
            />
          </Grid>
          {formData.payqr_enabled && (
            <Grid xs={12}>
              <Box
                sx={{
                  width: 100,
                  height: 100,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  borderRadius: "8px",
                  objectFit: "cover",
                  overflow: "hidden",
                  textAlign: "center",
                  border: formData.payqrUrl ? "none" : "2px dashed #ccc",
                }}
              >
                {formData.payqrUrl ? (
                  <img
                    src={formData.payqrUrl}
                    alt="Vista previa"
                    style={{
                      width: "100%",
                      borderRadius: "8px",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <RevaText id="Imagen del QR para pagos (.png)" variant="body1" />
                )}
              </Box>
              <Input
                type="file"
                inputProps={{ accept: "image/png" }}
                onChange={(e) =>
                  handleFileUpload(
                    e,
                    "payqr",
                    1000000,
                    intl.formatMessage({ id: "La imagen del QR no puede pesar más de 1MB" })
                  )
                }
              />
            </Grid>
          )}
          <Grid xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.signups_expires}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      signups_expires: e.target.checked,
                    })
                  }
                />
              }
              label={intl.formatMessage({ id: "Inscripciones sin pago expiran" })}
            />
          </Grid>
          <Grid xs={12}>
            {formData.signups_expires && (
              <Box display="flex" alignItems="center" gap={2}>
                <InputLabel id="paymentTimeLimit">{intl.formatMessage({ id: "Tiempo para pagar" })}</InputLabel>
                <FormControl size="small">
                  <Select
                    sx={{ width: 150 }}
                    labelId="paymentTimeLimit"
                    disabled={!formData.signups_expires}
                    value={formData.signups_expires_after}
                    onChange={(e) => setFormData({ ...formData, signups_expires_after: e.target.value })}
                  >
                    <MenuItem value={60}>{intl.formatMessage({ id: "1 hora" })}</MenuItem>
                    <MenuItem value={120}>{intl.formatMessage({ id: "2 horas" })}</MenuItem>
                    <MenuItem value={180}>{intl.formatMessage({ id: "3 horas" })}</MenuItem>
                    <MenuItem value={720}>{intl.formatMessage({ id: "12 horas" })}</MenuItem>
                    <MenuItem value={1440}>{intl.formatMessage({ id: "1 día" })}</MenuItem>
                    <MenuItem value={2880}>{intl.formatMessage({ id: "2 dias" })}</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            )}
          </Grid>
        </Grid>
      </SectionContainer>

      )}

      <SectionContainer title={stepTitles[3]}>
        <TextField
          label={intl.formatMessage({ id: "Link de WhatsApp" })}
          fullWidth
          margin="normal"
          value={formData.whatsapp_group_url}
          onChange={(e) =>
            setFormData({
              ...formData,
              whatsapp_group_url: e.target.value,
            })
          }
        />
        <TextField
          label={intl.formatMessage({ id: "Link de Discord" })}
          fullWidth
          margin="normal"
          value={formData.discord_url}
          onChange={(e) => setFormData({ ...formData, discord_url: e.target.value })}
        />
        <TextField
          label={intl.formatMessage({ id: "Link de formulario" })}
          fullWidth
          margin="normal"
          value={formData.form_url}
          onChange={(e) => setFormData({ ...formData, form_url: e.target.value })}
        />
      </SectionContainer>

      <Box sx={{ display: "flex", justifyContent: { xs: "flex-start", md: "flex-end" } }}>
        <CTAButton loading={submitting} variant="contained" onClick={() => saveChanges()}>
          {intl.formatMessage({ id: "Guardar cambios" })}
        </CTAButton>
      </Box>
    </Stack>
  );
}
